/**
 * Interactive Demo Entry Page
 *
 * Landing page for the guided interactive demo experience
 */

import React, { useState } from 'react';
import { motion } from 'motion/react';
import { GuidedFlow, registerFlow, mainDemoFlow } from '@/features/guided-flow';
import { DemoConversationProvider } from '@/features/guided-flow/contexts/DemoConversationContext';

// Register the main demo flow
registerFlow(mainDemoFlow);

export const InteractiveDemoPage: React.FC = () => {
  // Check if there's a step parameter in the URL - if so, skip landing page
  const [isDemoActive, setIsDemoActive] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('step');
  });

  const startDemo = () => {
    setIsDemoActive(true);
  };

  const handleDemoComplete = () => {
    setIsDemoActive(false);
    // Could redirect to signup or show completion modal
    console.log('Demo completed!');
  };

  const handleDemoCancel = () => {
    setIsDemoActive(false);
    console.log('Demo cancelled');
  };

  if (isDemoActive) {
    return (
      <DemoConversationProvider>
        <GuidedFlow
          flowId="main-platform-demo"
          autoStart={true}
          onComplete={handleDemoComplete}
          onCancel={handleDemoCancel}
        />
      </DemoConversationProvider>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-900 via-black to-neutral-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Logo */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="inline-block p-4 bg-primary/20 rounded-2xl backdrop-blur-sm border border-primary/30">
              <img
                src="/img/logo-40.svg"
                alt="Equal IQ"
                className="w-16 h-16"
              />
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-6xl font-bold mb-6 bg-gradient-to-r from-white via-white to-white/60 bg-clip-text text-transparent"
          >
            Welcome to Equal IQ
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-2xl text-white/70 mb-4"
          >
            The AI-powered platform for entertainment deal management
          </motion.p>

          {/* Feature highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex items-center justify-center gap-8 mb-12 text-white/60"
          >
            <div className="flex items-center gap-2">
              <span className="text-2xl">📄</span>
              <span>Contract Analysis</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🤝</span>
              <span>Team Collaboration</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">✍️</span>
              <span>E-Signature</span>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <button
              onClick={startDemo}
              className="group relative inline-flex items-center gap-3 px-12 py-5 bg-gradient-to-r from-primary to-primary/80 rounded-full text-white text-xl font-semibold hover:scale-105 transition-transform shadow-2xl shadow-primary/50"
            >
              <span className="text-3xl">🎬</span>
              <span>Start Interactive Demo</span>
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                →
              </motion.span>
            </button>

            <div className="mt-4 text-white/50 text-sm">
              5-minute guided tour • No signup required
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-16 grid grid-cols-3 gap-8 max-w-2xl mx-auto"
          >
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">10x</div>
              <div className="text-white/60 text-sm">Faster Deal Review</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">95%</div>
              <div className="text-white/60 text-sm">Error Reduction</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">$2M+</div>
              <div className="text-white/60 text-sm">Value Identified</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default InteractiveDemoPage;
